/**
 * The signed-in session: who the user is, their tenants, the one they act in and its role. Nobody
 * signed in: the browser goes to the BFF's sign-in, which returns here.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { needsStepUp, type Bff, type Me } from "../api/client";
import { useT } from "../i18n";
import { ErrorState, Loading } from "../ui/states";

type Leave = (url: string) => void;
const BffContext = createContext<{ bff: Bff; leave: Leave } | null>(null);

/** `leave` goes to a page outside the dashboard (the sign-in, the provider's logout). */
export function BffProvider({ bff, leave = (url) => window.location.assign(url), children }: {
  bff: Bff;
  leave?: Leave;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ bff, leave }), [bff, leave]);
  return <BffContext.Provider value={value}>{children}</BffContext.Provider>;
}

function useBffContext() {
  const context = useContext(BffContext);
  if (!context) throw new Error("outside BffProvider");
  return context;
}

export function useBff(): Bff {
  return useBffContext().bff;
}

export function useLeave(): Leave {
  return useBffContext().leave;
}

export const ME = ["bff", "me"] as const;

export function useMe(): Me {
  const { data } = useQuery({ queryKey: ME, queryFn: useBff().me, enabled: false });
  if (!data) throw new Error("useMe outside RequireSession");
  return data;
}

/** Whether the user's role allows `permission` here. The API decides; this only hides what would fail. */
export function useCan(permission: string): boolean {
  return useMe().permissions.includes(permission);
}

export function useCurrentTenant() {
  const me = useMe();
  return me.tenants.find((t) => t.id === me.tenant_id) ?? null;
}

/** Tenant-scoped queries start again after a switch: every key but the session's is dropped. */
export function useSwitchTenant(): (tenantId: string) => Promise<void> {
  const bff = useBff();
  const client = useQueryClient();
  const leave = useLeave();
  return async (tenantId) => {
    try {
      await bff.switchTenant(tenantId);
    } catch (error) {
      if (!needsStepUp(error)) throw error;
      leave(bff.loginUrl(window.location.pathname, { mfa: true })); // sign in with a second factor, then switch
      return;
    }
    client.removeQueries({ predicate: (q) => q.queryKey[0] !== "bff" });
    await client.invalidateQueries({ queryKey: ME });
  };
}

export function RequireSession({ children }: { children: ReactNode }) {
  const bff = useBff();
  const leave = useLeave();
  const t = useT();
  const me = useQuery({ queryKey: ME, queryFn: bff.me, staleTime: 60_000, retry: false });
  const signedOut = me.isSuccess && me.data === null;
  const queries = useQueryClient();
  useEffect(() => {
    if (signedOut) leave(bff.loginUrl(window.location.pathname + window.location.search));
  }, [signedOut, bff, leave]);
  useEffect(() => // a request refused for want of a second factor: sign in again with one (E12-T01)
    queries.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error" && needsStepUp(event.action.error)) {
        leave(bff.loginUrl(window.location.pathname + window.location.search, { mfa: true }));
      }
    }), [queries, bff, leave]);
  if (me.isPending) return <Loading label={t("app.loading")} />;
  if (me.isError) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;
  if (signedOut) return <Loading label={t("app.signingIn")} />;
  return <>{children}</>;
}
